import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Badge,
    Box,
    Button,
    Flex,
    HStack,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Spinner,
    Stack,
    Text,
    Tooltip,
    useToast,
    VStack,
} from "@chakra-ui/react";
import { AddIcon, ChevronDownIcon, ChevronUpIcon, SearchIcon } from "@chakra-ui/icons";
import { FiEdit2, FiEyeOff, FiPower } from "react-icons/fi";
import PreSessionQuestionsService from "../../services/PreSessionQuestions";

const TYPE_COLORS: Record<string, string> = {
    textarea: "purple",
    boolean: "blue",
    mcq: "orange",
    checkbox: "teal",
};

export const PreSessionQuestionList = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const token = localStorage.getItem("logged_in_user_token");
    const [list, setList] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState<"all" | "active" | "inactive">("all");

    const fetchData = async () => {
        setLoading(true);
        PreSessionQuestionsService.getAll(token)
            .then((res: any) => {
                setList(res.data?.data || []);
            })
            .catch((error: any) => {
                if (error?.response?.status === 401) {
                    toast({
                        position: "top",
                        title: "Authentication Failed.",
                        description: "Your session is expired.",
                        status: "error",
                        duration: 500,
                        isClosable: true,
                    });
                    setTimeout(() => navigate("/login"), 2000);
                }
                console.log(error);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchData();
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return list.filter((item) => {
            if (filter === "active" && !item.is_active) return false;
            if (filter === "inactive" && item.is_active) return false;
            if (!q) return true;
            const haystack = [
                item.question_text,
                item.helper_text,
                item.input_type,
                ...(item.options || []).map((o: any) => o.label),
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return haystack.includes(q);
        });
    }, [list, search, filter]);

    const activeCount = list.filter((q) => q.is_active).length;
    const requiredCount = list.filter((q) => q.is_required).length;

    const moveQuestion = async (indexInFullList: number, direction: "up" | "down") => {
        const targetIndex = direction === "up" ? indexInFullList - 1 : indexInFullList + 1;
        if (targetIndex < 0 || targetIndex >= list.length) return;

        const next = [...list];
        const [item] = next.splice(indexInFullList, 1);
        next.splice(targetIndex, 0, item);
        setList(next);

        try {
            const ids = next.map((q) => q.question_id);
            const res: any = await PreSessionQuestionsService.reorder(ids, token);
            setList(res.data?.data || next);
            toast({
                position: "top",
                title: "Order updated",
                status: "success",
                duration: 1500,
                isClosable: true,
            });
        } catch (error) {
            console.log(error);
            fetchData();
            toast({
                position: "top",
                title: "Failed to reorder",
                status: "error",
                duration: 2000,
                isClosable: true,
            });
        }
    };

    const toggleActive = async (question: any) => {
        try {
            await PreSessionQuestionsService.update(
                question.question_id,
                { is_active: !question.is_active },
                token
            );
            fetchData();
            toast({
                position: "top",
                title: question.is_active ? "Question disabled" : "Question enabled",
                status: "success",
                duration: 1200,
                isClosable: true,
            });
        } catch (error) {
            console.log(error);
            toast({
                position: "top",
                title: "Failed to update status",
                status: "error",
                duration: 2000,
                isClosable: true,
            });
        }
    };

    const deactivate = async (questionId: string) => {
        if (!window.confirm("Deactivate this question? It will be hidden from the mobile form.")) {
            return;
        }
        try {
            await PreSessionQuestionsService.deactivate(questionId, token);
            fetchData();
            toast({
                position: "top",
                title: "Question deactivated",
                status: "success",
                duration: 1500,
                isClosable: true,
            });
        } catch (error) {
            console.log(error);
            toast({
                position: "top",
                title: "Failed to deactivate",
                status: "error",
                duration: 2000,
                isClosable: true,
            });
        }
    };

    return (
        <div className="main-wrapper">
            <div className="page_title py-2">Pre-session Questions</div>
            <Box p={5} bg="#f5f7f7" minH="calc(100vh - 48px)">
                <Flex
                    justify="space-between"
                    align={{ base: "stretch", md: "center" }}
                    direction={{ base: "column", md: "row" }}
                    gap={4}
                    mb={5}
                >
                    <Box>
                        <Text fontSize="lg" fontWeight="700" color="#1f3d3d">
                            Mobile booking intake form
                        </Text>
                        <Text fontSize="sm" color="gray.600" mt={1}>
                            Manage questions shown after payment. Reorder, enable, or edit anytime.
                        </Text>
                        <HStack spacing={3} mt={3} flexWrap="wrap">
                            <Badge colorScheme="teal" px={3} py={1} borderRadius="full">
                                {list.length} total
                            </Badge>
                            <Badge colorScheme="green" px={3} py={1} borderRadius="full">
                                {activeCount} active
                            </Badge>
                            <Badge colorScheme="red" px={3} py={1} borderRadius="full">
                                {requiredCount} required
                            </Badge>
                        </HStack>
                    </Box>
                    <Button
                        leftIcon={<AddIcon />}
                        bg="#327171"
                        color="white"
                        _hover={{ bg: "#2a5f5f" }}
                        _active={{ bg: "#244f4f" }}
                        onClick={() => navigate("add")}
                        alignSelf={{ base: "stretch", md: "flex-start" }}
                        px={6}
                    >
                        Add Question
                    </Button>
                </Flex>

                <Flex
                    gap={3}
                    mb={4}
                    direction={{ base: "column", md: "row" }}
                    align={{ base: "stretch", md: "center" }}
                >
                    <InputGroup maxW={{ md: "360px" }} bg="white">
                        <InputLeftElement pointerEvents="none">
                            <SearchIcon color="gray.400" />
                        </InputLeftElement>
                        <Input
                            placeholder="Search questions, types, options..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            borderColor="#c9d6d6"
                            _focus={{ borderColor: "#327171", boxShadow: "0 0 0 1px #327171" }}
                        />
                    </InputGroup>
                    <HStack spacing={2}>
                        {(["all", "active", "inactive"] as const).map((key) => (
                            <Button
                                key={key}
                                size="sm"
                                variant={filter === key ? "solid" : "outline"}
                                bg={filter === key ? "#327171" : "white"}
                                color={filter === key ? "white" : "#327171"}
                                borderColor="#327171"
                                onClick={() => setFilter(key)}
                                textTransform="capitalize"
                            >
                                {key}
                            </Button>
                        ))}
                    </HStack>
                </Flex>

                {loading ? (
                    <Flex justify="center" py={16}>
                        <VStack spacing={3}>
                            <Spinner color="#327171" thickness="3px" size="lg" />
                            <Text color="gray.600">Loading questions...</Text>
                        </VStack>
                    </Flex>
                ) : !filtered.length ? (
                    <Box
                        bg="white"
                        border="1px dashed"
                        borderColor="#b7caca"
                        borderRadius="lg"
                        py={16}
                        px={6}
                        textAlign="center"
                    >
                        <Text fontSize="xl" fontWeight="700" color="#327171" mb={2}>
                            {list.length ? "No matching questions" : "No questions yet"}
                        </Text>
                        <Text color="gray.600" mb={6} maxW="420px" mx="auto">
                            {list.length
                                ? "Try a different search or filter."
                                : "Create the first question for the post-payment pre-session form. Seeded defaults appear once the experts-db service has run."}
                        </Text>
                        {!list.length ? (
                            <Button
                                leftIcon={<AddIcon />}
                                bg="#327171"
                                color="white"
                                _hover={{ bg: "#2a5f5f" }}
                                onClick={() => navigate("add")}
                            >
                                Add your first question
                            </Button>
                        ) : null}
                    </Box>
                ) : (
                    <Stack spacing={3}>
                        {filtered.map((question) => {
                            const fullIndex = list.findIndex(
                                (q) => q.question_id === question.question_id
                            );
                            return (
                                <Box
                                    key={question.question_id || question._id}
                                    bg="white"
                                    borderRadius="lg"
                                    border="1px solid"
                                    borderColor={question.is_active ? "#d5e0e0" : "#ecd9d9"}
                                    boxShadow="sm"
                                    p={4}
                                    opacity={question.is_active ? 1 : 0.78}
                                >
                                    <Flex
                                        gap={4}
                                        align={{ base: "stretch", md: "flex-start" }}
                                        direction={{ base: "column", md: "row" }}
                                    >
                                        <VStack spacing={1} minW="56px">
                                            <Tooltip label="Move up">
                                                <IconButton
                                                    aria-label="Move up"
                                                    icon={<ChevronUpIcon />}
                                                    size="sm"
                                                    variant="outline"
                                                    borderColor="#327171"
                                                    color="#327171"
                                                    isDisabled={fullIndex <= 0}
                                                    onClick={() => moveQuestion(fullIndex, "up")}
                                                />
                                            </Tooltip>
                                            <Text
                                                fontSize="sm"
                                                fontWeight="700"
                                                color="#327171"
                                                bg="#e8f2f2"
                                                px={2}
                                                py={0.5}
                                                borderRadius="md"
                                            >
                                                #{(question.sort_order ?? fullIndex) + 1}
                                            </Text>
                                            <Tooltip label="Move down">
                                                <IconButton
                                                    aria-label="Move down"
                                                    icon={<ChevronDownIcon />}
                                                    size="sm"
                                                    variant="outline"
                                                    borderColor="#327171"
                                                    color="#327171"
                                                    isDisabled={fullIndex >= list.length - 1}
                                                    onClick={() => moveQuestion(fullIndex, "down")}
                                                />
                                            </Tooltip>
                                        </VStack>

                                        <Box flex="1" minW={0}>
                                            <HStack spacing={2} mb={2} flexWrap="wrap">
                                                <Badge
                                                    colorScheme={TYPE_COLORS[question.input_type] || "gray"}
                                                    textTransform="uppercase"
                                                    letterSpacing="0.04em"
                                                >
                                                    {question.input_type}
                                                </Badge>
                                                {question.is_required ? (
                                                    <Badge colorScheme="red">Required</Badge>
                                                ) : (
                                                    <Badge colorScheme="gray">Optional</Badge>
                                                )}
                                                <Badge colorScheme={question.is_active ? "green" : "red"}>
                                                    {question.is_active ? "Active" : "Inactive"}
                                                </Badge>
                                            </HStack>
                                            <Text fontWeight="700" color="#1f3d3d" fontSize="md">
                                                {question.question_text}
                                            </Text>
                                            {question.helper_text ? (
                                                <Text fontSize="sm" color="gray.600" mt={1}>
                                                    {question.helper_text}
                                                </Text>
                                            ) : null}
                                            {(question.options || []).length > 0 ? (
                                                <HStack mt={3} spacing={2} flexWrap="wrap">
                                                    {(question.options || []).map((opt: any) => (
                                                        <Badge
                                                            key={opt.id}
                                                            variant="subtle"
                                                            colorScheme="teal"
                                                            px={2}
                                                            py={1}
                                                            borderRadius="md"
                                                        >
                                                            {opt.label}
                                                        </Badge>
                                                    ))}
                                                </HStack>
                                            ) : null}
                                            {question.link?.text ? (
                                                <Text fontSize="xs" color="#327171" mt={2}>
                                                    Link: {question.link.text}
                                                    {question.link.url ? ` → ${question.link.url}` : ""}
                                                </Text>
                                            ) : null}
                                        </Box>

                                        <HStack spacing={2} flexShrink={0} alignSelf={{ md: "center" }}>
                                            <Tooltip label="Edit">
                                                <IconButton
                                                    aria-label="Edit"
                                                    icon={<FiEdit2 />}
                                                    size="sm"
                                                    bg="#327171"
                                                    color="white"
                                                    _hover={{ bg: "#2a5f5f" }}
                                                    onClick={() => navigate(`edit/${question.question_id}`)}
                                                />
                                            </Tooltip>
                                            <Tooltip
                                                label={question.is_active ? "Disable" : "Enable"}
                                            >
                                                <IconButton
                                                    aria-label="Toggle active"
                                                    icon={<FiPower />}
                                                    size="sm"
                                                    variant="outline"
                                                    borderColor="#327171"
                                                    color="#327171"
                                                    onClick={() => toggleActive(question)}
                                                />
                                            </Tooltip>
                                            <Tooltip label="Deactivate">
                                                <IconButton
                                                    aria-label="Deactivate"
                                                    icon={<FiEyeOff />}
                                                    size="sm"
                                                    variant="outline"
                                                    borderColor="red.400"
                                                    color="red.500"
                                                    onClick={() => deactivate(question.question_id)}
                                                />
                                            </Tooltip>
                                        </HStack>
                                    </Flex>
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </Box>
        </div>
    );
};